"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu } from "lucide-react";
import { usePathname } from "next/navigation";
import { SidebarProvider, useSidebar } from "@/contexts/sidebar-context";
import { ToastProvider } from "@/contexts/toast-context";
import { ThemeProvider } from "@/contexts/theme-context";
import { Sidebar } from "./sidebar";
import { ToastContainer } from "./toast";
import { Logo } from "./logo";
import type { ReactNode } from "react";

const spring = { type: "spring" as const, stiffness: 400, damping: 30 };

// --- Splash Screen with assembling hex-tile logo ---

// Each teal hex: [points, seed for scatter direction]
const SHIELD_HEXES = [
  // Folder Tab (3)
  "76,19 106,2 136,19 136,53 106,70 76,53",
  "136,19 166,2 196,19 196,53 166,70 136,53",
  "196,19 226,2 256,19 256,53 226,70 196,53",
  // Row 0 (7)
  "46,70 76,53 106,70 106,104 76,121 46,104",
  "106,70 136,53 166,70 166,104 136,121 106,104",
  "166,70 196,53 226,70 226,104 196,121 166,104",
  "226,70 256,53 286,70 286,104 256,121 226,104",
  "286,70 316,53 346,70 346,104 316,121 286,104",
  "346,70 376,53 406,70 406,104 376,121 346,104",
  "406,70 436,53 466,70 466,104 436,121 406,104",
  // Row 1 (6)
  "76,121 106,104 136,121 136,155 106,172 76,155",
  "136,121 166,104 196,121 196,155 166,172 136,155",
  "196,121 226,104 256,121 256,155 226,172 196,155",
  "256,121 286,104 316,121 316,155 286,172 256,155",
  "316,121 346,104 376,121 376,155 346,172 316,155",
  "376,121 406,104 436,121 436,155 406,172 376,155",
  // Row 2 (7)
  "46,172 76,155 106,172 106,206 76,223 46,206",
  "106,172 136,155 166,172 166,206 136,223 106,206",
  "166,172 196,155 226,172 226,206 196,223 166,206",
  "226,172 256,155 286,172 286,206 256,223 226,206",
  "286,172 316,155 346,172 346,206 316,223 286,206",
  "346,172 376,155 406,172 406,206 376,223 346,206",
  "406,172 436,155 466,172 466,206 436,223 406,206",
  // Row 3 (6)
  "76,223 106,206 136,223 136,257 106,274 76,257",
  "136,223 166,206 196,223 196,257 166,274 136,257",
  "196,223 226,206 256,223 256,257 226,274 196,257",
  "256,223 286,206 316,223 316,257 286,274 256,257",
  "316,223 346,206 376,223 376,257 346,274 316,257",
  "376,223 406,206 436,223 436,257 406,274 376,257",
  // Row 4 (5)
  "106,274 136,257 166,274 166,308 136,325 106,308",
  "166,274 196,257 226,274 226,308 196,325 166,308",
  "226,274 256,257 286,274 286,308 256,325 226,308",
  "286,274 316,257 346,274 346,308 316,325 286,308",
  "346,274 376,257 406,274 406,308 376,325 346,308",
  // Row 5 (4)
  "136,325 166,308 196,325 196,359 166,376 136,359",
  "196,325 226,308 256,325 256,359 226,376 196,359",
  "256,325 286,308 316,325 316,359 286,376 256,359",
  "316,325 346,308 376,325 375,359 346,376 316,359",
  // Row 6 (3)
  "166,376 196,359 226,376 226,410 196,427 166,410",
  "226,376 256,359 286,376 286,410 256,427 226,410",
  "286,376 316,359 346,376 346,410 316,427 286,410",
  // Row 7 (2)
  "196,427 226,410 256,427 256,461 226,478 196,461",
  "256,427 286,410 316,427 316,461 286,478 256,461",
  // Row 8 (1)
  "226,478 256,461 286,478 286,512 256,529 226,512",
];

const BLOCK_HEXES = [
  "237,130 256,119 275,130 275,152 256,163 237,152",
  "250,163 256,159 262,163 262,175 256,179 250,175",
  "237,232 256,221 275,232 275,254 256,265 237,254",
  "250,265 256,261 262,265 262,277 256,281 250,277",
  "237,334 256,323 275,334 275,356 256,367 237,356",
];

// Deterministic scatter offsets based on hex index (no random/useRef needed)
function scatterOffset(i: number): { x: number; y: number; r: number } {
  const angle = ((i * 137.5) % 360) * (Math.PI / 180); // golden angle spread
  const dist = 250 + (i % 7) * 40;
  return {
    x: Math.cos(angle) * dist,
    y: Math.sin(angle) * dist,
    r: ((i * 47) % 360) - 180,
  };
}

function SplashScreen({ onDone }: { onDone: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onDone, 2400);
    return () => clearTimeout(timer);
  }, [onDone]);

  const totalHexes = SHIELD_HEXES.length;

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{ background: "var(--background)" }}
    >
      {/* Blurred ambient glow */}
      <motion.div
        className="absolute w-72 h-72 rounded-full"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.15 }}
        transition={{ duration: 1, delay: 0.3 }}
        style={{
          background: "radial-gradient(circle, #0d9488 0%, transparent 70%)",
          filter: "blur(60px)",
        }}
      />

      <div className="flex flex-col items-center gap-5 relative z-10">
        {/* Assembling logo */}
        <svg
          width={80}
          height={80 * (532 / 512)}
          viewBox="0 0 512 532"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {SHIELD_HEXES.map((pts, i) => {
            const { x, y, r } = scatterOffset(i);
            return (
              <motion.polygon
                key={i}
                points={pts}
                fill="#0d9488"
                initial={{ opacity: 0, x, y, rotate: r, scale: 0.3 }}
                animate={{ opacity: 1, x: 0, y: 0, rotate: 0, scale: 1 }}
                transition={{
                  delay: 0.08 + (i / totalHexes) * 0.7,
                  duration: 0.6,
                  type: "spring",
                  stiffness: 180,
                  damping: 18,
                }}
              />
            );
          })}
          {BLOCK_HEXES.map((pts, i) => (
            <motion.polygon
              key={`b${i}`}
              points={pts}
              fill="white"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                delay: 1.0 + i * 0.08,
                duration: 0.3,
                type: "spring",
                stiffness: 300,
                damping: 20,
              }}
            />
          ))}
        </svg>

        {/* Brand name — appears after logo assembles */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1.2, ease: [0.23, 1, 0.32, 1] }}
          className="flex flex-col items-center"
        >
          <span className="text-xl font-bold tracking-tight">
            <span className="text-foreground">Chain</span>
            <span className="text-[#0d9488]">Filer</span>
          </span>
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 1.5 }}
            className="text-[10px] text-muted uppercase tracking-[0.14em] mt-1"
          >
            On-Chain Intelligence
          </motion.span>
        </motion.div>

        {/* Loading dots */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="flex gap-1 mt-1"
        >
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-[#0d9488]"
              animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.1, 0.8] }}
              transition={{
                duration: 0.9,
                repeat: Infinity,
                delay: i * 0.15,
                ease: "easeInOut",
              }}
            />
          ))}
        </motion.div>
      </div>
    </motion.div>
  );
}

function ShellInner({ children }: { children: ReactNode }) {
  const { collapsed, isMobile, mobileOpen, setMobileOpen, hoverExpanded } = useSidebar();
  const pathname = usePathname();

  // Guest pages: no sidebar, no mobile top bar, no margin
  const isGuestPage = pathname.startsWith("/lock/") || (pathname.startsWith("/kyc/") && pathname !== "/kyc");
  if (isGuestPage) {
    return <>{children}</>;
  }

  // Main content margin follows the *persisted* collapsed state, not hover state.
  // This prevents the main content from jumping when the sidebar hover-expands.
  const marginLeft = isMobile ? 0 : collapsed ? 68 : 220;

  return (
    <>
      <Sidebar />

      {/* Mobile top bar */}
      {isMobile && (
        <div
          className="fixed top-0 left-0 right-0 h-14 z-30 flex items-center px-3 gap-2.5"
          style={{
            background: "var(--surface)",
            backdropFilter: "blur(16px) saturate(140%)",
            WebkitBackdropFilter: "blur(16px) saturate(140%)",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <button
            aria-label="Open menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(true)}
            className="btn-icon p-1.5 rounded-md text-muted hover:text-foreground transition-all duration-150 cursor-pointer"
          >
            <Menu className="w-5 h-5" />
          </button>
          <Logo size={22} />
          <span className="text-[13px] font-bold text-foreground tracking-tight">
            <span>Chain</span><span className="text-[#0d9488]">Filer</span>
          </span>
        </div>
      )}

      <motion.main
        animate={{ marginLeft }}
        transition={spring}
        className={`min-h-screen ${isMobile ? "pt-14" : ""}`}
      >
        {children}
      </motion.main>
    </>
  );
}

export function LayoutShell({ children }: { children: ReactNode }) {
  const [showSplash, setShowSplash] = useState(true);

  return (
    <ThemeProvider>
      <SidebarProvider>
        <ToastProvider>
          <AnimatePresence>
            {showSplash && <SplashScreen onDone={() => setShowSplash(false)} />}
          </AnimatePresence>
          <ShellInner>{children}</ShellInner>
          <ToastContainer />
        </ToastProvider>
      </SidebarProvider>
    </ThemeProvider>
  );
}
